import { MarkCircle, MarkLine, MarkLineLike } from '../mathsKit';
import { MathsKitTool } from '../types';
import { GeometryUtils } from './geometry';
import { VecLike, Vec2d } from './Vec2d';
import { CircleSegment } from './CircleSegment';

export interface BindDrawOptions {
  getGlobalScale:()=>number;
  getInstances:()=> Map<string, MathsKitTool>;
  activeDraw:(bol:boolean)=>void;
  getForwardingTarget:()=>HTMLElement;
  getStrokeWidth:()=>number;
  getActiveManager:()=>boolean;
}

export type ActiveMathsKit = (MarkLine | MarkCircle) & {
  mathsKit: MathsKitTool;
}

export class DrawManager {
  /** 最小响应距离，单位px */
  static readonly MIN_DISTANCE = 20;
  static readonly eventForwardMap: Map<string, string[]> = new Map([
    ['pointerdown', ['pointermove', 'pointerup']],
    ['mousedown', ['mousemove', 'mouseup']],
  ]);
  private element: HTMLDivElement;
  private options: BindDrawOptions;
  private activeMathsKit: ActiveMathsKit | null = null;
  private removeEventClockTimer: number | undefined = undefined;
  private _isForwarding: boolean = false;
  private startDrawPoint: Vec2d | null = null;

  set isForwarding(isForwarding: boolean) {
    this._isForwarding = isForwarding;
    this.options.activeDraw(isForwarding);
  }
  get isForwarding(): boolean {
    return this._isForwarding;
  }

  constructor(element:HTMLDivElement, options:BindDrawOptions) {
    this.element = element;
    this.options = options;
    this.element.addEventListener('pointerdown', this.onPointerdown, true);
    this.element.addEventListener('wheel', this.onWheel, true);
  }

  private onWheel = (e: WheelEvent) => {  
    if (this.options.getActiveManager()) {
      e.stopPropagation();
      // todo 缩放功能后期实现
      this.forwardEvent(e);
      e.preventDefault();
    }
  };

  private generateActiveMathsKit(point: VecLike) {
    let minDistance = DrawManager.MIN_DISTANCE / this.options.getGlobalScale();
    let minDistanceMathsKit: MathsKitTool | undefined = undefined;
    let minDistanceMarkLine: MarkLineLike | undefined = undefined;
    for (const instance of this.options.getInstances().values()) {
      const markLines = instance.getMarkLines();
      if (markLines.length > 0) {
        for (const markLine of markLines) {
          let distance = 0;
          if (markLine.axis === 'circle') {
            distance = GeometryUtils.pointToCircleDistance(point, (markLine as MarkCircle).circle);
          } else {
            distance = GeometryUtils.pointToLineDistance(point, (markLine as MarkLine).line);
          }
          if (distance < minDistance) {
            minDistance = distance;
            minDistanceMarkLine = markLine;
            minDistanceMathsKit = instance;
          }
        }
      }
    }
    if (minDistanceMarkLine && minDistanceMathsKit) {
      this.activeMathsKit = {
        ...minDistanceMarkLine,
        mathsKit: minDistanceMathsKit,
      };
    } else {
      this.activeMathsKit = null;
    }
  }
  
  private onPointerdown = (e:PointerEvent) => {
    const currentTarget = e.target as HTMLElement;
    if (!currentTarget) {
      return;
    }
    if (currentTarget !== this.element) {
      return;
    }
    this.options.getInstances().forEach(instance => {
      instance.setActive(false);
    });
    this.generateActiveMathsKit(new Vec2d(e.offsetX, e.offsetY));
    this.isForwarding = true;
    if (!this.removeEventClockTimer) {
      for (const [startEvent, events] of DrawManager.eventForwardMap.entries()) {
        this.element.addEventListener(startEvent, this.onDrawContainerStartEventListener as EventListener);
        for (const event of events) {
          window.addEventListener(event, this.onDrawDocumentEventListener as EventListener, true);
        }
      }
    }
    if (this.removeEventClockTimer) {
      clearTimeout(this.removeEventClockTimer);
      this.removeEventClockTimer = undefined;
    }
  };

  private onDrawContainerStartEventListener = (e: PointerEvent | MouseEvent) => {
    if (e.target !== this.element) {
      return;
    }
    e.stopPropagation();
    this.onDrawEventListener(e);
  };

  private onDrawDocumentEventListener = (e: PointerEvent | MouseEvent) => {
    if (!this.isForwarding) {
      return;
    }
    if (e.target === this.options.getForwardingTarget()) {
      return;
    }
    e.stopPropagation();
    // if (e instanceof TouchEvent) {
    //   if (e.type === 'touchend' && e.changedTouches.length > 1) {
    //     this.forwardEvent(e);
    //     return;
    //   } else if (e.touches.length > 1) {
    //     this.forwardEvent(e);
    //     return;
    //   }
    // }
    this.onDrawEventListener(e);
    if (e.type === 'pointerup') {
      this.onDrawEndHandler();
    }
  };

  private onDrawEndHandler = () => {
    if (this.removeEventClockTimer) {
      clearTimeout(this.removeEventClockTimer);
      this.removeEventClockTimer = undefined;
    }
    this.removeEventClockTimer = setTimeout(() => {
      this.removeEventListener();
    }, 100) as unknown as number;
  };


  private generateLinePoint(e: PointerEvent | MouseEvent, param: MarkLine, mathsKit: MathsKitTool) {
    const { axis, direction, line } = param;
    let offsetDistance = 0;
    switch (direction) {
      case 'bottom':
      case 'left': {
        offsetDistance = -this.options.getStrokeWidth() / 2 * this.options.getGlobalScale();
        break;
      } 
      case 'right': 
      case 'top':
      case 'topLeftToBottomRight':
      case 'topRightToBottomLeft':
      {
        offsetDistance = this.options.getStrokeWidth() / 2 * this.options.getGlobalScale();
        break;
      }
    }
    let clientPoint = new Vec2d(0, 0);
    let screenPoint = new Vec2d(0, 0);
    // if(e instanceof TouchEvent){
    //   if (e.touches.length > 0) {
    //     clientPoint = new Vec2d(e.touches[0].clientX, e.touches[0].clientY);
    //     screenPoint = new Vec2d(e.touches[0].screenX, e.touches[0].screenY);
    //   } else if (e.changedTouches.length > 0) {
    //     clientPoint = new Vec2d(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    //     screenPoint = new Vec2d(e.changedTouches[0].screenX, e.changedTouches[0].screenY);
    //   }
    // } else {
    clientPoint = new Vec2d(e.clientX, e.clientY);
    screenPoint = new Vec2d(e.screenX, e.screenY);
    // }
    // 根据e.clientX, e.clientY计算出相对于container的offsetX, offsetY坐标
    const containerRect = this.element.getBoundingClientRect();
    const eventOffsetX= clientPoint.x - containerRect.left;
    const eventOffsetY = clientPoint.y - containerRect.top;
    const point = new Vec2d(eventOffsetX, eventOffsetY);
    const newOffsetPoint = GeometryUtils.getProjectionPointWithOffset(
      point, 
      line, 
      axis === 'x' ? 'x' : 'y', 
      offsetDistance,
    );
    // 根据eventOffsetX, eventOffsetY和e.clientX, e.clientY计算出偏移量,再把projectionPoint加上这个偏移量使其变成clientX, clientY相同坐标系下的点
    const offsetX = Math.round(clientPoint.x - eventOffsetX);
    const offsetY = Math.round(clientPoint.y - eventOffsetY);
    const newClientPoint = newOffsetPoint.add(new Vec2d(offsetX, offsetY));
    const offsetVector = new Vec2d(clientPoint.x - newClientPoint.x, clientPoint.y - newClientPoint.y);
    const newPoints = {
      clientPoint: newClientPoint,
      screenPoint: screenPoint.add(offsetVector),
    };
    if (e.type === 'pointerdown') {
      this.startDrawPoint = newPoints.clientPoint;
    } else if (e.type === 'pointerup') {
      this.startDrawPoint = null;
    }
    if (this.startDrawPoint) {
      const distance = GeometryUtils.distance(this.startDrawPoint, newPoints.clientPoint);
      mathsKit.showLineLength(distance / this.options.getGlobalScale());
    }
    return newPoints;
  }
  private generateCirclePoint(e: PointerEvent | MouseEvent, param: MarkCircle, _mathsKit: MathsKitTool) {    
    let clientPoint = new Vec2d(0, 0);
    let screenPoint = new Vec2d(0, 0);
    // if(e instanceof TouchEvent){
    //   if (e.touches.length > 0) {
    //     clientPoint = new Vec2d(e.touches[0].clientX, e.touches[0].clientY);
    //     screenPoint = new Vec2d(e.touches[0].screenX, e.touches[0].screenY);
    //   } else if (e.changedTouches.length > 0) {
    //     clientPoint = new Vec2d(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    //     screenPoint = new Vec2d(e.changedTouches[0].screenX, e.changedTouches[0].screenY);
    //   }
    // } else {
    clientPoint = new Vec2d(e.clientX, e.clientY);
    screenPoint = new Vec2d(e.screenX, e.screenY);
    // }
    
    // 根据e.clientX, e.clientY计算出相对于container的offsetX, offsetY坐标
    const containerRect = this.element.getBoundingClientRect();
    const eventOffsetX = clientPoint.x - containerRect.left;
    const eventOffsetY = clientPoint.y - containerRect.top;
    const point = new Vec2d(eventOffsetX, eventOffsetY);
    const { circle } = param;
    const circleToJson = CircleSegment.toJson(circle);
    const offsetDistance = this.options.getStrokeWidth() / 2 * this.options.getGlobalScale();
    const radius = circle.radius + offsetDistance;
    circleToJson.radius = radius;
    // 转换为 CircleSegment 实例
    const arcInstance = CircleSegment.fromJson(circleToJson);
    arcInstance.setRadius(radius);
    
    // 找到圆弧上距离该点最近的点
    const newNearestPoint = arcInstance.nearestPointOnSegment(point);
    
    // 计算偏移量，使 nearestPoint 变成 clientX, clientY 相同坐标系下的点
    const offsetX = Math.round(clientPoint.x - eventOffsetX);
    const offsetY = Math.round(clientPoint.y - eventOffsetY);
    const newClientPoint = newNearestPoint.add(new Vec2d(offsetX, offsetY));
    
    // 计算偏移向量
    const offsetVector = new Vec2d(clientPoint.x - newClientPoint.x, clientPoint.y - newClientPoint.y);
    const newPoints = {
      clientPoint: newClientPoint,
      screenPoint: screenPoint.add(offsetVector),
    };
    
    return newPoints;
  }
  private onDrawEventListener = (e: PointerEvent | MouseEvent) => {
    if (!this.activeMathsKit) {
      this.forwardEvent(e);
      return;
    }
    let newPoints: { clientPoint: Vec2d, screenPoint: Vec2d } | undefined = undefined;
    const { axis, mathsKit } = this.activeMathsKit;
    if (axis === 'circle') {
      const { circle } = this.activeMathsKit as MarkCircle;
      newPoints = this.generateCirclePoint(e, { axis, circle } as MarkCircle, mathsKit);
    } else {
      const { line, direction } = this.activeMathsKit as MarkLine;
      newPoints = this.generateLinePoint(e, { axis, line, direction } as MarkLine, mathsKit);
    }
    this.forwardEvent(e, newPoints, 0);
  };

  private forwardEvent(e: PointerEvent | MouseEvent | WheelEvent, _newPoints?: { clientPoint: Vec2d, screenPoint: Vec2d }, _touchIndex?: number) {
    let newEvent: PointerEvent | MouseEvent | WheelEvent = e;
    let newPoints: { clientPoint: Vec2d, screenPoint: Vec2d } | undefined = _newPoints;
    if (e instanceof PointerEvent) {
      if (!newPoints) {
        newPoints = {
          clientPoint: new Vec2d(e.clientX, e.clientY),
          screenPoint: new Vec2d(e.screenX, e.screenY),
        };
      }
      newEvent = new PointerEvent(e.type, {
        bubbles: true,
        cancelable: true,
        composed: true,
        clientX: newPoints.clientPoint.x,
        clientY: newPoints.clientPoint.y,
        screenX: newPoints.screenPoint.x,
        screenY: newPoints.screenPoint.y,
        buttons: e.buttons,
        button: e.button,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        metaKey: e.metaKey,
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        pressure: e.pressure,
      });
    } else if (e instanceof WheelEvent) {
      newEvent = new WheelEvent('wheel', {
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        deltaMode: e.deltaMode, // 像素
        bubbles: true,
        cancelable: true,
        view: window,
      });
    } else if (e instanceof MouseEvent) {
      if (!newPoints) {
        newPoints = {
          clientPoint: new Vec2d(e.clientX, e.clientY),
          screenPoint: new Vec2d(e.screenX, e.screenY),
        };
      }
      newEvent = new MouseEvent(e.type, {
        bubbles: true,
        cancelable: true,
        composed: true,
        clientX: newPoints.clientPoint.x,
        clientY: newPoints.clientPoint.y,
        screenX: newPoints.screenPoint.x,
        screenY: newPoints.screenPoint.y,
        buttons: e.buttons,
        button: e.button,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        metaKey: e.metaKey,
      });
    }
    this.options.getForwardingTarget().dispatchEvent(newEvent); 
  }

  private removeEventListener() {
    this.isForwarding = false;
    this.activeMathsKit = null;
    this.startDrawPoint = null;
    this.removeEventClockTimer = undefined;
    for (const [startEvent, events] of DrawManager.eventForwardMap.entries()) {
      this.element.removeEventListener(startEvent, this.onDrawContainerStartEventListener as EventListener);
      for (const event of events) {
        window.removeEventListener(event, this.onDrawDocumentEventListener as EventListener, true);
      }
    }
    this.options.getInstances().forEach(instance => {
      instance.setActive(this.options.getActiveManager());
    });
  }

  private stop(){
    if (this.removeEventClockTimer) {
      clearTimeout(this.removeEventClockTimer);
      this.removeEventClockTimer = undefined;
    }
    this.removeEventListener();
  }

  destroy(){
    this.stop();
    this.element.removeEventListener('pointerdown', this.onPointerdown, true);
    this.element.removeEventListener('wheel', this.onWheel, true);
  }

}